import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import Page from '../Page';
import Loading from '../../components/shared/Loading';
import MediaUpload from '../../components/shared/DirectUpload';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_MEAL, UPDATE_MEAL } from '../../graphql/nutrition';
import ServerError from '../error/ServerError';
import config from '../../config';

const mealTypes = ['lunch', 'breakfast', 'dinner', 'snack', 'dessert', 'custom'];

const secondsToMinutes = (seconds) =>
  seconds ? Math.max(1, Math.round(seconds / 60)) : null;

const titleCase = (value = '') =>
  value.replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatNumber = (value, suffix = '') =>
  value == null || value === '' || Number.isNaN(Number(value))
    ? '-'
    : `${Math.round(Number(value))}${suffix}`;

const computeMacrosFromIngredients = (recipeIngredients = []) =>
  recipeIngredients.reduce(
    (totals, ingredient) => ({
      fatGrams: totals.fatGrams + Number(ingredient.macros?.fatGrams || 0),
      carbGrams: totals.carbGrams + Number(ingredient.macros?.carbGrams || 0),
      proteinGrams: totals.proteinGrams + Number(ingredient.macros?.proteinGrams || 0),
      calories: totals.calories + Number(ingredient.macros?.calories || 0),
    }),
    { fatGrams: 0, carbGrams: 0, proteinGrams: 0, calories: 0 }
  );

const getMealType = (meal) =>
  (meal.tags || []).find((tag) => mealTypes.includes(tag.toLowerCase())) || '';

const getCoverImageUrl = (meal) =>
  meal.coverImageUrl || meal.coverImages?.[0]?.url || '';

const getMealPreviewRows = (meal) => [
  [
    'Ingredients',
    (meal.recipeIngredients || [])
      .map((ingredient) => ingredient.name)
      .filter(Boolean)
      .join(', ') || 'None',
  ],
  ['Instructions', `${meal.recipeInstructions?.length || 0} simple steps`],
  ['Tags', meal.tags?.length ? meal.tags : ['None']],
  ['Allergens', meal.allergies?.length ? meal.allergies.join(', ') : 'None detected'],
];

const MealEditor = () => {
  const params = useParams();
  const [meal, setMeal] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const id = params.id;
  const { error, loading, data } = useQuery(GET_MEAL, { variables: { id } });
  const [updateMeal] = useMutation(UPDATE_MEAL, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    },
    onCompleted: (data) => {
      setMeal(data?.updateMeal || meal);
      enqueueSnackbar('Meal saved', {
        variant: 'success',
        autoHideDuration: 1600,
      });
    },
  });

  useEffect(() => {
    setMeal(data?.meal);
  }, [data]);

  const coverImageUrl = useMemo(() => (meal ? getCoverImageUrl(meal) : ''), [meal]);
  const mealType = meal ? getMealType(meal) : '';
  const prepMinutes = secondsToMinutes(meal?.prepTimeSeconds);
  const cookMinutes = secondsToMinutes(meal?.cookTimeSeconds);

  const buildInput = (mealInput) => ({
    title: mealInput.title,
    tags: mealInput.tags || [],
    allergies: mealInput.allergies || [],
    desc: mealInput.desc,
    status: mealInput.status,
    prepTimeSeconds: parseInt(mealInput.prepTimeSeconds || 0, 10),
    cookTimeSeconds: parseInt(mealInput.cookTimeSeconds || 0, 10),
    recipeYield: parseInt(mealInput.recipeYield || 1, 10),
    isUsingIngredientMacros: mealInput.isUsingIngredientMacros || false,
    macros: {
      fatGrams: parseFloat(mealInput.macros?.fatGrams || 0),
      proteinGrams: parseFloat(mealInput.macros?.proteinGrams || 0),
      carbGrams: parseFloat(mealInput.macros?.carbGrams || 0),
      calories: parseFloat(mealInput.macros?.calories || 0),
    },
    coverImages: mealInput.coverImages || [],
    recipeIngredients: (mealInput.recipeIngredients || []).map((ingredient) => ({
      name: ingredient.name,
      quantity: parseFloat(ingredient.quantity || 0),
      quantityUnit: ingredient.quantityUnit,
      macros: {
        fatGrams: parseFloat(ingredient.macros?.fatGrams || 0),
        proteinGrams: parseFloat(ingredient.macros?.proteinGrams || 0),
        carbGrams: parseFloat(ingredient.macros?.carbGrams || 0),
        calories: parseFloat(ingredient.macros?.calories || 0),
      },
    })),
    recipeInstructions: (mealInput.recipeInstructions || []).map((instruction) => ({
      text: instruction.text,
    })),
  });

  const handleSave = (overrides = {}) => {
    if (!meal) return;
    const mealToSave = { ...meal, ...overrides };
    enqueueSnackbar('Saving...', { autoHideDuration: 1200 });
    updateMeal({
      variables: {
        id: mealToSave._id,
        input: buildInput(mealToSave),
      },
    });
  };

  const handleChange = (event) => {
    let newMeal = _.cloneDeep(meal);
    newMeal = _.set(newMeal, event.target.name, event.target.value);
    setMeal(newMeal);
  };

  const handleMealTypeChange = (selectedType) => {
    const tagsWithoutTypes = (meal.tags || []).filter(
      (tag) => !mealTypes.includes(tag.toLowerCase())
    );
    setMeal({
      ...meal,
      tags: selectedType ? [...tagsWithoutTypes, selectedType] : tagsWithoutTypes,
    });
  };

  const handleAutoCalculateChange = (event) => {
    const isUsingIngredientMacros = event.target.checked;
    setMeal({
      ...meal,
      isUsingIngredientMacros,
      macros: isUsingIngredientMacros
        ? computeMacrosFromIngredients(meal.recipeIngredients || [])
        : meal.macros,
    });
  };

  const handleUploadError = () => {
    enqueueSnackbar('Media upload failed', {
      variant: 'error',
      autoHideDuration: 2000,
    });
  };

  const handleUploadSuccess = (uploadedResponse) => {
    setMeal({ ...meal, coverImages: [uploadedResponse.image] });
  };

  const handleUploadRemove = () => {
    setMeal({ ...meal, coverImages: [] });
  };

  if (error) return <ServerError message={error.message} />;
  if (loading || !meal) return <Loading />;

  const macros = [
    { value: formatNumber(meal.macros?.calories), label: 'kcal' },
    { value: formatNumber(meal.macros?.proteinGrams, 'g'), label: 'Protein' },
    { value: formatNumber(meal.macros?.carbGrams, 'g'), label: 'Carbs' },
    { value: formatNumber(meal.macros?.fatGrams, 'g'), label: 'Fat' },
  ];

  return (
    <Page title={`Meal Editor : ${config.appDisplayName}`}>
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography sx={{ color: '#40536b', fontSize: 14, mb: 1 }}>
              Nutrition &nbsp;›&nbsp; Meals &nbsp;›&nbsp;{' '}
              <Box component="span" sx={{ color: '#4338ca', fontWeight: 700 }}>
                Create Meal
              </Box>
            </Typography>
            <Typography sx={{ color: '#071120', fontSize: 30, fontWeight: 800 }}>
              Create Meal
            </Typography>
            <Typography sx={{ color: '#40536b', mt: 0.75 }}>
              Describe your meal and we&apos;ll handle the rest.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ color: '#17213f', fontWeight: 600 }}>
                Auto-calculate
              </Typography>
              <Switch
                checked={!!meal.isUsingIngredientMacros}
                color="secondary"
                onChange={handleAutoCalculateChange}
              />
            </Stack>
            <Button variant="outlined" onClick={() => handleSave({ status: 'DRAFT' })}>
              Save as Draft
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSave()}
              sx={{ minWidth: 180 }}
            >
              Save Meal
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={7.4}>
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.75 }}>
                    1. Tell us about your meal
                  </Typography>
                  <Typography sx={{ color: '#40536b', mb: 2.5 }}>
                    Give your meal a name and describe it. Our AI will generate ingredients,
                    nutrition, and more.
                  </Typography>

                  <Typography sx={{ color: '#17213f', fontWeight: 700, mb: 1 }}>
                    Meal Name <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    name="title"
                    onChange={handleChange}
                    required
                    value={meal.title || ''}
                  />

                  <Typography sx={{ color: '#17213f', fontWeight: 700, mt: 3, mb: 0.5 }}>
                    Describe your meal <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                  </Typography>
                  <Typography sx={{ color: '#40536b', mb: 1 }}>
                    What&apos;s in it? How is it made? Add as much or as little detail as you want.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    name="desc"
                    onChange={handleChange}
                    required
                    rows={5}
                    value={meal.desc || ''}
                  />
                  <Typography align="right" sx={{ color: '#40536b', fontSize: 13, mt: 0.75 }}>
                    {(meal.desc || '').length}/500
                  </Typography>

                  <Typography sx={{ color: '#17213f', fontWeight: 700, mt: 2, mb: 1.5 }}>
                    Meal Type <Box component="span" sx={{ color: '#64748b', fontWeight: 500 }}>(optional)</Box>
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    {mealTypes.map((type) => {
                      const selected = mealType.toLowerCase() === type;
                      return (
                        <Button
                          key={type}
                          variant={selected ? 'contained' : 'outlined'}
                          color={selected ? 'secondary' : 'inherit'}
                          onClick={() => handleMealTypeChange(type)}
                          sx={{
                            minWidth: 140,
                            borderColor: selected ? '#6d5dfc' : '#dbe3ee',
                            color: selected ? '#fff' : '#17213f',
                          }}
                        >
                          {type === 'custom' ? '•••' : null}&nbsp;{titleCase(type)}
                        </Button>
                      );
                    })}
                  </Stack>

                  <Typography sx={{ color: '#17213f', fontWeight: 700, mt: 3, mb: 1 }}>
                    Add a photo <Box component="span" sx={{ color: '#64748b', fontWeight: 500 }}>(optional)</Box>
                  </Typography>
                  <Box
                    sx={{
                      alignItems: 'center',
                      border: '1px solid #dbe3ee',
                      borderRadius: 1,
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', sm: '260px 1fr' },
                      p: 1.5,
                    }}
                  >
                    <MediaUpload
                      images={meal.coverImages || []}
                      onError={handleUploadError}
                      onSuccess={handleUploadSuccess}
                      onRemove={handleUploadRemove}
                      maxFiles={1}
                      style={{
                        border: '1px dashed #cbd5e1',
                        borderRadius: 8,
                        minHeight: 104,
                      }}
                    />
                    <Box sx={{ color: '#40536b', textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography sx={{ color: '#17213f', fontWeight: 700 }}>
                        Drag &amp; drop or click to upload
                      </Typography>
                      <Typography>JPG, PNG or WebP (max. 5MB)</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

            </Stack>
          </Grid>

          <Grid item xs={12} lg={4.6}>
            <Card variant="outlined" sx={{ borderColor: '#dbe3ee', borderRadius: 1, position: 'sticky', top: 16 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Meal Preview</Typography>
                  <Button
                    variant="outlined"
                    endIcon={<LaunchIcon />}
                    href={meal.webUrl}
                    target="_blank"
                    disabled={!meal.webUrl}
                  >
                    Preview in app
                  </Button>
                </Stack>
                <Box sx={{ aspectRatio: '16 / 7', bgcolor: '#f1f5f9', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                  {coverImageUrl ? (
                    <Box
                      component="img"
                      src={coverImageUrl}
                      alt={meal.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: '#64748b' }}>
                      No cover image
                    </Stack>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 19, fontWeight: 800 }}>
                    {meal.title || 'Untitled meal'}
                  </Typography>
                  {mealType && (
                    <Chip
                      size="small"
                      label={titleCase(mealType)}
                      sx={{ bgcolor: '#f5f3ff', color: '#4338ca', fontWeight: 700 }}
                    />
                  )}
                </Stack>
                <Typography sx={{ color: '#40536b', lineHeight: 1.6, mb: 2 }}>
                  {meal.desc || 'Add a description to preview it here.'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container>
                  {macros.map(({ value, label }, index) => (
                    <Grid
                      item
                      xs={3}
                      key={label}
                      sx={{
                        borderRight: index < macros.length - 1 ? '1px solid #e2e8f0' : 'none',
                        textAlign: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{value}</Typography>
                      <Typography sx={{ color: '#64748b', fontSize: 13 }}>{label}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-around" sx={{ color: '#40536b', mb: 2 }}>
                  <Typography>{meal.recipeYield || 1} serving</Typography>
                  <Typography>Prep {prepMinutes ?? '-'} min</Typography>
                  <Typography>Cook {cookMinutes ?? '-'} min</Typography>
                </Stack>
                <Divider />
                {getMealPreviewRows(meal).map(([label, value]) => (
                  <Box key={label}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.75 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{label}</Typography>
                        {Array.isArray(value) ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {value.map((item) => (
                              <Chip key={item} size="small" label={titleCase(item)} />
                            ))}
                          </Stack>
                        ) : (
                          <Typography sx={{ color: '#40536b' }}>{value}</Typography>
                        )}
                      </Box>
                      <ExpandMoreIcon />
                    </Stack>
                    <Divider />
                  </Box>
                ))}
                <Box
                  sx={{
                    bgcolor: '#f5f3ff',
                    border: '1px solid #c4b5fd',
                    borderRadius: 1,
                    color: '#4338ca',
                    mt: 2,
                    p: 2,
                  }}
                >
                  Tip: You can edit any of the details above after generation.
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default MealEditor;
